import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDate } from '../lib/api';
import { esMovil, urlParaAbrirFuera } from '../lib/utils';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { useAuth } from '../lib/auth';
import {
  Folder, FolderPlus, FolderOpen, Upload, FileText, FileSpreadsheet, Image as ImageIcon,
  File as FileIcon, Download, Pencil, Trash2, Loader2, ChevronRight, HardDrive, Search, Eye, MoveRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** "1.2 MB" a partir de bytes. */
function tamano(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function tipoDoc(doc) {
  const mime = doc.mime || '';
  const nombre = (doc.name || '').toLowerCase();
  if (mime === 'application/pdf' || nombre.endsWith('.pdf')) {
    return { Icono: FileText, color: 'text-red-600', fondo: 'bg-red-50 dark:bg-red-950/30' };
  }
  if (mime.startsWith('image/')) {
    return { Icono: ImageIcon, color: 'text-purple-600', fondo: 'bg-purple-50 dark:bg-purple-950/30' };
  }
  if (/\.(xlsx?|csv)$/.test(nombre) || mime.includes('spreadsheet') || mime.includes('excel')) {
    return { Icono: FileSpreadsheet, color: 'text-emerald-600', fondo: 'bg-emerald-50 dark:bg-emerald-950/30' };
  }
  if (/\.(docx?)$/.test(nombre) || mime.includes('word')) {
    return { Icono: FileText, color: 'text-sky-600', fondo: 'bg-sky-50 dark:bg-sky-950/30' };
  }
  return { Icono: FileIcon, color: 'text-muted-foreground', fondo: 'bg-muted' };
}

/**
 * Icono dentro de un cuadrado teñido de su color (2026-07-27). Antes el glifo
 * iba suelto sobre el fondo y la lista se leía como un listado de texto.
 */
function FichaTipo({ doc, size = 36 }) {
  const { Icono, color, fondo } = tipoDoc(doc);
  return (
    <span style={{ width: size, height: size }}
      className={`shrink-0 rounded-[10px] inline-flex items-center justify-center ${fondo}`}>
      <Icono className={`w-[55%] h-[55%] ${color}`} />
    </span>
  );
}

const PREVISUALIZABLES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export default function DrivePage() {
  // Administradores (y dirección) organizan; los administrativos solo suben a
  // la bandeja general y ven lo que hay en ella (el backend lo impone igual).
  const { user } = useAuth();
  const organiza = ['admin', 'direccion'].includes((user?.role || '').toLowerCase());
  // Carpeta actual (null = raíz) + ruta de migas calculada del árbol completo.
  const [carpetaActual, setCarpetaActual] = useState(null);
  const [todasCarpetas, setTodasCarpetas] = useState([]);
  const [contenido, setContenido] = useState({ folders: [], docs: [], busqueda: false });
  const [cargando, setCargando] = useState(true);
  const [q, setQ] = useState('');

  const [nuevaCarpetaOpen, setNuevaCarpetaOpen] = useState(false);
  const [nuevaCarpetaNombre, setNuevaCarpetaNombre] = useState('');
  const [subiendo, setSubiendo] = useState(0); // nº de ficheros en vuelo
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Renombrar / mover (carpeta o documento)
  const [editando, setEditando] = useState(null); // {tipo: 'carpeta'|'doc', item, nombre, destino}
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  const [uso, setUso] = useState(null); // {usado, cuota, libre}

  const cargarUso = useCallback(() => {
    api.get('/api/drive/uso').then((r) => setUso(r.data)).catch(() => {});
  }, []);
  useEffect(() => { cargarUso(); }, [cargarUso]);

  const cargarArbol = useCallback(() => {
    api.get('/api/drive/carpetas')
      .then((r) => setTodasCarpetas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = q.trim() ? { q: q.trim() } : { folder_id: carpetaActual || undefined };
      const res = await api.get('/api/drive', { params });
      setContenido(res.data || { folders: [], docs: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el Drive');
    } finally {
      setCargando(false);
    }
  }, [carpetaActual, q]);

  useEffect(() => { cargarArbol(); }, [cargarArbol]);
  useEffect(() => { const t = setTimeout(cargar, q ? 300 : 0); return () => clearTimeout(t); }, [cargar, q]);

  // Migas: de la carpeta actual hacia arriba.
  const migas = useMemo(() => {
    const porId = Object.fromEntries(todasCarpetas.map((c) => [c.id, c]));
    const ruta = [];
    let cursor = carpetaActual;
    while (cursor && porId[cursor]) {
      ruta.unshift(porId[cursor]);
      cursor = porId[cursor].parent_id;
    }
    return ruta;
  }, [carpetaActual, todasCarpetas]);

  // Opciones del selector "mover a": todas las carpetas con su ruta legible.
  const opcionesCarpetas = useMemo(() => {
    const porId = Object.fromEntries(todasCarpetas.map((c) => [c.id, c]));
    const rutaDe = (id) => {
      const partes = [];
      let cursor = id;
      let guarda = 0;
      while (cursor && porId[cursor] && guarda < 20) {
        partes.unshift(porId[cursor].name);
        cursor = porId[cursor].parent_id;
        guarda += 1;
      }
      return partes.join(' / ');
    };
    return todasCarpetas
      .map((c) => ({ id: c.id, ruta: rutaDe(c.id) }))
      .sort((a, b) => a.ruta.localeCompare(b.ruta));
  }, [todasCarpetas]);

  const crearCarpeta = async () => {
    const nombre = nuevaCarpetaNombre.trim();
    if (!nombre) return;
    try {
      await api.post('/api/drive/carpetas', { name: nombre, parent_id: carpetaActual });
      setNuevaCarpetaOpen(false);
      setNuevaCarpetaNombre('');
      cargarArbol();
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la carpeta');
    }
  };

  const subirFicheros = async (files) => {
    const lista = Array.from(files || []).filter(Boolean);
    if (!lista.length) return;
    setSubiendo((n) => n + lista.length);
    for (const f of lista) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        await api.post('/api/drive/upload', fd, {
          params: carpetaActual ? { folder_id: carpetaActual } : {},
        });
        toast.success(`Subido: ${f.name}`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'no se pudo subir'}`);
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
    cargar();
    cargarUso();
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    const { tipo, item, nombre, destino } = editando;
    const url = tipo === 'carpeta' ? `/api/drive/carpetas/${item.id}` : `/api/drive/docs/${item.id}`;
    const body = {};
    if (nombre.trim() && nombre.trim() !== item.name) body.name = nombre.trim();
    const campoDestino = tipo === 'carpeta' ? 'parent_id' : 'folder_id';
    const destinoActual = item[campoDestino] || '';
    if ((destino || '') !== destinoActual) body[campoDestino] = destino || null;
    if (!Object.keys(body).length) { setEditando(null); return; }
    try {
      await api.put(url, body);
      toast.success('Guardado');
      setEditando(null);
      cargarArbol();
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    }
  };

  const borrarCarpeta = async (c) => {
    if (!window.confirm(`¿Eliminar la carpeta «${c.name}»? (debe estar vacía)`)) return;
    try {
      await api.delete(`/api/drive/carpetas/${c.id}`);
      toast.success('Carpeta eliminada');
      cargarArbol();
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const borrarDoc = async (d) => {
    if (!window.confirm(`¿Eliminar «${d.name}»? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/api/drive/docs/${d.id}`);
      toast.success('Documento eliminado');
      cargar();
      cargarUso();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const urlDescarga = (d) => `${BACKEND_URL}/api/drive/docs/${d.id}/download`;
  // En el móvil el archivo se abre FUERA de la app, y ahí no llega la cookie de
  // sesión (iOS le da a la app del icono sus propias cookies). Por eso se usa
  // el enlace firmado que manda el servidor con el listado (2026-07-28).
  const urlAbrir = (d) => {
    const base = d.token_abrir
      ? `${BACKEND_URL}/api/drive/docs/${d.id}/abrir?t=${encodeURIComponent(d.token_abrir)}`
      : urlDescarga(d);
    // Desde el icono de la pantalla de inicio (standalone) hay que saltar a
    // Safari de verdad: un .docx/.xlsx no lo abre nadie dentro del icono —no
    // hay gestor de descargas ni visor de Office—, y ahí sí (2026-07-28).
    return urlParaAbrirFuera(base);
  };

  // Arrastre INTERNO: mover un documento a una carpeta soltándolo encima
  // (o sobre una miga para subirlo de nivel). Convive con el arrastre de
  // ficheros desde el escritorio, que sigue subiendo a la carpeta actual.
  const [docArrastrado, setDocArrastrado] = useState(null);       // doc en vuelo
  const [carpetaArrastrada, setCarpetaArrastrada] = useState(null); // carpeta en vuelo
  const [sobreCarpeta, setSobreCarpeta] = useState(null);         // destino bajo el cursor
  const hayArrastreInterno = !!(docArrastrado || carpetaArrastrada);

  const moverDoc = async (docId, folderId) => {
    setSobreCarpeta(null);
    try {
      await api.put(`/api/drive/docs/${docId}`, { folder_id: folderId || null });
      toast.success('Documento movido');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo mover');
    }
  };

  // ---- Arrastrar CARPETAS enteras (2026-08-27) ----------------
  // Se recorre lo soltado con la API de entradas del navegador: los archivos
  // se apuntan con su ruta de carpetas, el árbol se recrea en el Drive
  // (carpetas/asegurar: existente o creada) y cada archivo va a la suya.
  const _leerLote = (reader) => new Promise((res, rej) => reader.readEntries(res, rej));

  const _recogerEntry = async (entry, ruta, salida) => {
    if (!entry) return;
    if (entry.isFile) {
      const file = await new Promise((res, rej) => entry.file(res, rej));
      salida.push({ file, ruta });
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      // readEntries devuelve lotes de <=100: hay que repetir hasta vaciar.
      let lote = await _leerLote(reader);
      while (lote.length) {
        for (const hijo of lote) {
          // eslint-disable-next-line no-await-in-loop
          await _recogerEntry(hijo, [...ruta, entry.name], salida);
        }
        // eslint-disable-next-line no-await-in-loop
        lote = await _leerLote(reader);
      }
    }
  };

  const subirArrastre = async (dataTransfer) => {
    const items = Array.from(dataTransfer?.items || []);
    const entries = items
      .map((it) => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null));
    const hayCarpeta = entries.some((en) => en?.isDirectory);
    if (!hayCarpeta) {
      subirFicheros(dataTransfer.files);
      return;
    }
    const pares = [];
    try {
      for (const en of entries) {
        // eslint-disable-next-line no-await-in-loop
        await _recogerEntry(en, [], pares);
      }
    } catch {
      toast.error('No se pudo leer la carpeta arrastrada');
      return;
    }
    if (!pares.length) {
      toast.info('La carpeta arrastrada está vacía');
      return;
    }

    // Los administrativos no pueden crear carpetas: todo a la bandeja general.
    if (!organiza) {
      toast.info('Sin permiso de carpetas: los archivos van a la bandeja general');
      subirFicheros(pares.map((par) => par.file));
      return;
    }

    setSubiendo((n) => n + pares.length);
    const cacheCarpetas = new Map(); // 'a/b/c' → folder_id
    let subidos = 0;
    let fallos = 0;
    const carpetasCreadas = new Set();
    for (const { file, ruta } of pares) {
      try {
        let padre = carpetaActual || null;
        for (let i = 0; i < ruta.length; i += 1) {
          const clave = `${carpetaActual || ''}//${ruta.slice(0, i + 1).join('/')}`;
          if (!cacheCarpetas.has(clave)) {
            // eslint-disable-next-line no-await-in-loop
            const r = await api.post('/api/drive/carpetas/asegurar', {
              name: ruta[i],
              parent_id: padre,
            });
            cacheCarpetas.set(clave, r.data.id);
            carpetasCreadas.add(r.data.id);
          }
          padre = cacheCarpetas.get(clave);
        }
        const fd = new FormData();
        fd.append('file', file);
        // eslint-disable-next-line no-await-in-loop
        await api.post('/api/drive/upload', fd, {
          params: padre ? { folder_id: padre } : {},
        });
        subidos += 1;
      } catch (e2) {
        fallos += 1;
        toast.error(`${file.name}: ${e2?.response?.data?.detail || 'no se pudo subir'}`);
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
    if (subidos) {
      toast.success(`Carpeta subida: ${subidos} archivo${subidos === 1 ? '' : 's'} en ${carpetasCreadas.size} carpeta${carpetasCreadas.size === 1 ? '' : 's'}${fallos ? ` (${fallos} fallaron)` : ''}`);
    }
    cargar();
    cargarUso();
    cargarArbol();
  };

  const esArrastreInterno = (e) =>
    e.dataTransfer?.types?.includes('text/drive-doc-id')
    || e.dataTransfer?.types?.includes('text/drive-folder-id');

  const moverCarpeta = async (folderId, destinoId) => {
    setSobreCarpeta(null);
    if (folderId === destinoId) return;
    try {
      await api.put(`/api/drive/carpetas/${folderId}`, { parent_id: destinoId || null });
      toast.success('Carpeta movida');
      cargarArbol();
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo mover la carpeta');
    }
  };

  // Un drop puede traer un documento o una carpeta: se resuelve aquí.
  const soltarEn = (e, destinoId) => {
    const docId = e.dataTransfer.getData('text/drive-doc-id');
    const carpetaId = e.dataTransfer.getData('text/drive-folder-id');
    if (docId) { e.preventDefault(); e.stopPropagation(); moverDoc(docId, destinoId); }
    else if (carpetaId) { e.preventDefault(); e.stopPropagation(); moverCarpeta(carpetaId, destinoId); }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!esArrastreInterno(e)) setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (esArrastreInterno(e)) return; // lo gestionan las carpetas/migas
        subirArrastre(e.dataTransfer);
      }}
    >
      <PageHeader
        title="ClinicPilot Drive"
        subtitle={organiza
          ? 'Documentos de la clínica, organizados por carpetas. Se guardan con la copia de seguridad.'
          : 'Bandeja general: sube aquí los documentos y administración los organiza en carpetas.'}
        actions={
          <>
            {organiza && (
              <Button variant="outline" onClick={() => setNuevaCarpetaOpen(true)} data-testid="drive-nueva-carpeta">
                <FolderPlus className="w-4 h-4 mr-2" /> Nueva carpeta
              </Button>
            )}
            <Button onClick={() => inputRef.current?.click()} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="drive-subir">
              {subiendo > 0 ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Subir documentos
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { subirFicheros(e.target.files); e.target.value = ''; }}
              data-testid="drive-input-ficheros"
            />
          </>
        }
      />

      {/* Contador de espacio: usado / cuota, con barra. Ámbar desde el 80%,
          rojo desde el 95%. */}
      {uso && (
        <div className="mb-4 max-w-md" data-testid="drive-uso">
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="text-muted-foreground">
              <strong className="text-[#13231E]">{tamano(uso.usado)}</strong> usados de {tamano(uso.cuota)}
            </span>
            <span className={
              uso.usado / uso.cuota >= 0.95 ? 'font-semibold text-red-700'
                : uso.usado / uso.cuota >= 0.8 ? 'font-semibold text-amber-600'
                : 'text-muted-foreground'
            }>
              {tamano(uso.libre)} libres
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                uso.usado / uso.cuota >= 0.95 ? 'bg-red-600'
                  : uso.usado / uso.cuota >= 0.8 ? 'bg-amber-500'
                  : 'bg-[#2E6E5E]'
              }`}
              style={{ width: `${Math.min(100, Math.max(1, (uso.usado / uso.cuota) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {/* Buscador + migas */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar en todo el Drive…"
            className="pl-8 w-64"
            data-testid="drive-buscar"
          />
        </div>
        {!q && (
          <nav className="flex items-center gap-1 text-sm" data-testid="drive-migas">
            <button
              type="button"
              onClick={() => setCarpetaActual(null)}
              onDragOver={(e) => { if (hayArrastreInterno) { e.preventDefault(); setSobreCarpeta('raiz'); } }}
              onDragLeave={() => setSobreCarpeta((v) => (v === 'raiz' ? null : v))}
              onDrop={(e) => soltarEn(e, null)}
              className={`inline-flex items-center gap-1 hover:underline rounded px-1 ${!carpetaActual ? 'font-semibold text-[#13231E]' : 'text-muted-foreground'} ${sobreCarpeta === 'raiz' ? 'ring-2 ring-[#2E6E5E] bg-[rgba(46,110,94,0.08)]' : ''}`}
            >
              <HardDrive className="w-4 h-4" /> Drive
            </button>
            {migas.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1">
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                <button
                  type="button"
                  onClick={() => setCarpetaActual(c.id)}
                  onDragOver={(e) => { if (hayArrastreInterno) { e.preventDefault(); setSobreCarpeta(c.id); } }}
                  onDragLeave={() => setSobreCarpeta((v) => (v === c.id ? null : v))}
                  onDrop={(e) => soltarEn(e, c.id)}
                  className={`hover:underline rounded px-1 ${carpetaActual === c.id ? 'font-semibold text-[#13231E]' : 'text-muted-foreground'} ${sobreCarpeta === c.id ? 'ring-2 ring-[#2E6E5E] bg-[rgba(46,110,94,0.08)]' : ''}`}
                >
                  {c.name}
                </button>
              </span>
            ))}
          </nav>
        )}
      </div>

      <div className={`cp-card overflow-hidden ${dragOver ? 'ring-2 ring-[#2E6E5E] ring-offset-2' : ''}`}>
        {dragOver && (
          <div className="p-3 text-center text-sm font-medium text-[#2E6E5E] bg-[rgba(46,110,94,0.06)]">
            Suelta aquí los ficheros para subirlos a esta carpeta
          </div>
        )}
        {cargando ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : contenido.folders.length === 0 && contenido.docs.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={q ? 'Sin resultados' : 'Carpeta vacía'}
            description={q ? 'Nada coincide con esa búsqueda.' : 'Sube documentos o crea una carpeta para empezar.'}
          />
        ) : (
          <div className="divide-y divide-border/50">
            {contenido.folders.map((c) => (
              <div
                key={c.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/drive-folder-id', c.id);
                  e.dataTransfer.effectAllowed = 'move';
                  const ghost = document.createElement('div');
                  ghost.textContent = `📁 ${c.name.length > 30 ? `${c.name.slice(0, 30)}…` : c.name}`;
                  ghost.style.cssText = 'position:absolute;top:0;left:-9999px;max-width:240px;padding:5px 10px;background:#2E6E5E;color:#fff;font-size:12px;font-weight:600;border-radius:999px;box-shadow:0 6px 16px rgba(0,0,0,.3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
                  document.body.appendChild(ghost);
                  try { e.dataTransfer.setDragImage(ghost, 16, 16); } catch {}
                  setTimeout(() => ghost.remove(), 0);
                  setCarpetaArrastrada(c.id);
                }}
                onDragEnd={() => { setCarpetaArrastrada(null); setSobreCarpeta(null); }}
                onDragOver={(e) => {
                  if (hayArrastreInterno && carpetaArrastrada !== c.id) { e.preventDefault(); setSobreCarpeta(c.id); }
                }}
                onDragLeave={() => setSobreCarpeta((v) => (v === c.id ? null : v))}
                onDrop={(e) => soltarEn(e, c.id)}
                className={`flex items-center gap-3 px-4 py-2.5 group cursor-grab active:cursor-grabbing transition-colors duration-150 hover:bg-[rgba(46,110,94,0.06)] ${sobreCarpeta === c.id ? 'ring-2 ring-inset ring-[#2E6E5E] bg-[rgba(46,110,94,0.10)]' : ''} ${carpetaArrastrada === c.id ? 'opacity-40' : ''}`}
                data-testid="drive-carpeta"
              >
                <button
                  type="button"
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  onClick={() => { setQ(''); setCarpetaActual(c.id); }}
                >
                  <span className="shrink-0 w-9 h-9 rounded-[10px] bg-[rgba(46,110,94,0.10)]
                    inline-flex items-center justify-center">
                    <Folder className="w-5 h-5 text-[#2E6E5E]" />
                  </span>
                  <span className="font-medium text-[#13231E] truncate">{c.name}</span>
                </button>
                <span className="text-xs text-muted-foreground hidden sm:block">{c.created_by_name}</span>
                {organiza && (
                <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Renombrar o mover"
                    onClick={() => setEditando({ tipo: 'carpeta', item: c, nombre: c.name, destino: c.parent_id || '' })}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" title="Eliminar"
                    onClick={() => borrarCarpeta(c)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
              </div>
            ))}
            {contenido.docs.map((d) => (
              <div
                key={d.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/drive-doc-id', d.id);
                  e.dataTransfer.effectAllowed = 'move';
                  const ghost = document.createElement('div');
                  ghost.textContent = d.name.length > 34 ? `${d.name.slice(0, 34)}…` : d.name;
                  ghost.style.cssText = 'position:absolute;top:0;left:-9999px;max-width:240px;padding:5px 10px;background:#2E6E5E;color:#fff;font-size:12px;font-weight:600;border-radius:999px;box-shadow:0 6px 16px rgba(0,0,0,.3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
                  document.body.appendChild(ghost);
                  try { e.dataTransfer.setDragImage(ghost, 16, 16); } catch {}
                  setTimeout(() => ghost.remove(), 0);
                  setDocArrastrado(d.id);
                }}
                onDragEnd={() => { setDocArrastrado(null); setSobreCarpeta(null); }}
                onClick={() => {
                  // En móvil no se abre desde aquí: lo hace el enlace de
                  // verdad que cubre la fila (ver abajo). iOS ignora
                  // `window.open` desde el icono de la pantalla de inicio.
                  if (esMovil()) return;
                  if (PREVISUALIZABLES.includes(d.mime)) {
                    setPreview({ open: true, url: urlDescarga(d), filename: d.name, title: d.name });
                  } else {
                    const a = document.createElement('a');
                    a.href = urlDescarga(d);
                    a.download = d.name;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  }
                }}
                className={`relative flex items-center gap-3 px-4 py-2.5 group cursor-pointer sm:cursor-grab active:cursor-grabbing transition-colors duration-150 hover:bg-[rgba(46,110,94,0.06)] ${docArrastrado === d.id ? 'opacity-40' : ''}`}
                data-testid="drive-doc"
              >
                {/* Enlace real que cubre toda la fila, solo en móvil: es la
                    única forma de que el archivo se abra desde el icono del
                    iPhone, donde el JavaScript de abrir pestañas no funciona
                    y la cookie de sesión no viaja (2026-07-28). */}
                {esMovil() && (
                  <a
                    href={urlAbrir(d)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 z-[1]"
                    aria-label={`Abrir ${d.name}`}
                    data-testid="drive-abrir-movil"
                  />
                )}
                <FichaTipo doc={d} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#13231E] truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {tamano(d.size)} · {d.created_by_name} · {formatDate((d.created_at || '').slice(0, 10))}
                  </div>
                </div>
                <div className="relative z-[2] flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}>
                  {PREVISUALIZABLES.includes(d.mime) && !esMovil() && (
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Ver"
                      onClick={() => setPreview({ open: true, url: urlDescarga(d), filename: d.name, title: d.name })}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {/* En móvil también es un enlace de verdad, por lo mismo. */}
                  {esMovil() ? (
                    <a
                      href={urlAbrir(d)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-[rgba(46,110,94,0.08)]"
                      data-testid="drive-descargar"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Descargar"
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = urlDescarga(d);
                        a.download = d.name;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      }}
                      data-testid="drive-descargar">
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {organiza && (
                    <>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Renombrar o mover"
                        onClick={() => setEditando({ tipo: 'doc', item: d, nombre: d.name, destino: d.folder_id || '' })}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" title="Eliminar"
                        onClick={() => borrarDoc(d)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nueva carpeta */}
      <Dialog open={nuevaCarpetaOpen} onOpenChange={setNuevaCarpetaOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nueva carpeta</DialogTitle></DialogHeader>
          <div>
            <Label>Nombre</Label>
            <Input
              value={nuevaCarpetaNombre}
              onChange={(e) => setNuevaCarpetaNombre(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') crearCarpeta(); }}
              placeholder="Facturas 2026, Contratos, Protocolos…"
              autoFocus
              data-testid="drive-carpeta-nombre"
            />
            {carpetaActual && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Se creará dentro de «{migas[migas.length - 1]?.name}».
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuevaCarpetaOpen(false)}>Cancelar</Button>
            <Button onClick={crearCarpeta} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="drive-carpeta-crear">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renombrar / mover */}
      <Dialog open={!!editando} onOpenChange={(v) => { if (!v) setEditando(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editando?.tipo === 'carpeta' ? 'Carpeta' : 'Documento'}: renombrar o mover</DialogTitle>
          </DialogHeader>
          {editando && (
            <div className="space-y-3">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={editando.nombre}
                  onChange={(e) => setEditando((s) => ({ ...s, nombre: e.target.value }))}
                  data-testid="drive-editar-nombre"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><MoveRight className="w-3.5 h-3.5" /> Mover a</Label>
                <Select
                  value={editando.destino || 'raiz'}
                  onValueChange={(v) => setEditando((s) => ({ ...s, destino: v === 'raiz' ? '' : v }))}
                >
                  <SelectTrigger data-testid="drive-editar-destino"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="raiz">Drive (raíz)</SelectItem>
                    {opcionesCarpetas
                      .filter((o) => !(editando.tipo === 'carpeta' && o.id === editando.item.id))
                      .map((o) => <SelectItem key={o.id} value={o.id}>{o.ruta}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditando(null)}>Cancelar</Button>
            <Button onClick={guardarEdicion} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="drive-editar-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        open={preview.open}
        onOpenChange={(v) => setPreview((s) => ({ ...s, open: v }))}
        url={preview.url}
        filename={preview.filename}
        title={preview.title}
      />
    </div>
  );
}
